import { AIMessage } from '@langchain/core/messages';
import type { ToolCall } from '@langchain/core/messages/tool';
import { StructuredToolInterface } from '@langchain/core/tools';
import { createProgressChannel } from '../utils/progress-channel.js';
import { all } from '../utils/concurrency.js';
import { discoverSkills } from '../skills/index.js';
import { withCreditScope } from '../tools/finance/credit-usage.js';
import { isBillingError } from '../utils/errors.js';
import type {
  ApprovalDecision,
  ToolApprovalEvent,
  ToolDeniedEvent,
  ToolEndEvent,
  ToolErrorEvent,
  ToolLimitEvent,
  ToolProgressEvent,
  ToolStartEvent,
} from './types.js';
import type { Question, UserAnswers } from '../tools/ask-user-question/types.js';
import { markCreditExhausted, type RunContext } from './run-context.js';

type ToolExecutionEvent =
  | ToolStartEvent
  | ToolProgressEvent
  | ToolEndEvent
  | ToolErrorEvent
  | ToolApprovalEvent
  | ToolDeniedEvent
  | ToolLimitEvent;

const TOOLS_REQUIRING_APPROVAL = ['write_file', 'edit_file'] as const;
const DEFAULT_MAX_CONCURRENCY = 10;

interface ToolCallBatch {
  concurrent: boolean;
  calls: ToolCall[];
}

/**
 * Executes tool calls with concurrent support for read-only tools.
 *
 * Consecutive concurrent-safe tool calls are batched and run in parallel
 * (up to maxConcurrency). Non-concurrent tools execute serially with
 * approval gates where required.
 */
export class AgentToolExecutor {
  private readonly sessionApprovedTools: Set<string>;
  private readonly maxConcurrency: number;

  constructor(
    private readonly toolMap: Map<string, StructuredToolInterface>,
    private readonly concurrencyMap: Map<string, boolean>,
    private readonly signal?: AbortSignal,
    private readonly requestToolApproval?: (request: {
      tool: string;
      args: Record<string, unknown>;
    }) => Promise<ApprovalDecision>,
    sessionApprovedTools?: Set<string>,
    maxConcurrency?: number,
    private readonly requestUserInput?: (request: {
      questions: Question[];
    }) => Promise<UserAnswers>,
    /**
     * Resolves tools activated on demand at runtime (MCP tools surfaced by
     * `search_tools`). These are not in the fixed toolMap, so name lookups and
     * concurrency decisions fall back to this provider.
     */
    private readonly activeToolsProvider?: () => StructuredToolInterface[],
  ) {
    this.sessionApprovedTools = sessionApprovedTools ?? new Set();
    this.maxConcurrency = maxConcurrency ?? DEFAULT_MAX_CONCURRENCY;
  }

  /**
   * Execute all tool calls from an AIMessage response.
   * Concurrent-safe tools run in parallel batches; others run serially.
   */
  async *executeAll(
    response: AIMessage,
    ctx: RunContext,
  ): AsyncGenerator<ToolExecutionEvent, void> {
    const batches = this.partitionToolCalls(response.tool_calls!, ctx);

    for (const batch of batches) {
      if (batch.concurrent && batch.calls.length > 1) {
        yield* this.executeBatchConcurrently(batch.calls, ctx);
      } else {
        for (const call of batch.calls) {
          yield* this.executeSingleWithId(call, ctx);
        }
      }
    }
  }

  /**
   * Partition tool_calls into batches of consecutive concurrent-safe calls
   * vs individual non-concurrent calls.
   */
  private partitionToolCalls(toolCalls: ToolCall[], ctx: RunContext): ToolCallBatch[] {
    const batches: ToolCallBatch[] = [];

    for (const rawCall of toolCalls) {
      const call = this.normalizeSkillToolCall(rawCall);

      // Skill dedup — skip already-executed skills
      if (call.name === 'skill') {
        const skillName = (call.args as Record<string, unknown>).skill as string;
        if (ctx.scratchpad.hasExecutedSkill(skillName)) continue;
      }

      // Runtime MCP tools aren't in the concurrency map; they are read-only, so
      // default them to concurrency-safe.
      const isSafe = this.concurrencyMap.get(call.name) ?? this.isActiveTool(call.name);
      const lastBatch = batches[batches.length - 1];

      if (isSafe && lastBatch?.concurrent) {
        lastBatch.calls.push(call);
      } else {
        batches.push({ concurrent: isSafe, calls: [call] });
      }
    }

    return batches;
  }

  /**
   * Execute a batch of concurrent-safe tools in parallel.
   */
  private async *executeBatchConcurrently(
    calls: ToolCall[],
    ctx: RunContext,
  ): AsyncGenerator<ToolExecutionEvent, void> {
    const generators = calls.map(call => this.executeSingleWithId(call, ctx));
    yield* all(generators, this.maxConcurrency);
  }

  /**
   * Execute a single tool call, emitting toolCallId on every event.
   */
  private async *executeSingleWithId(
    rawCall: ToolCall,
    ctx: RunContext,
  ): AsyncGenerator<ToolExecutionEvent, void> {
    const call = this.normalizeSkillToolCall(rawCall);
    const toolName = call.name;
    const toolArgs = call.args as Record<string, unknown>;
    const toolCallId = call.id;
    const toolQuery = this.extractQueryFromArgs(toolArgs);

    if (ctx.creditExhausted) {
      const message = `Skipped because available credits are exhausted. Answer from already collected data instead of making more tool calls.`;
      yield { type: 'tool_error', tool: toolName, error: message, toolCallId };
      ctx.scratchpad.recordToolCall(toolName, toolQuery);
      ctx.scratchpad.addToolResult(toolName, toolArgs, `Error: ${message}`);
      return;
    }

    // Approval flow for sensitive tools
    if (this.requiresApproval(toolName) && !this.sessionApprovedTools.has(toolName)) {
      const decision = (await this.requestToolApproval?.({ tool: toolName, args: toolArgs })) ?? 'deny';
      yield { type: 'tool_approval', tool: toolName, args: toolArgs, approved: decision };
      if (decision === 'deny') {
        yield { type: 'tool_denied', tool: toolName, args: toolArgs, toolCallId };
        return;
      }
      if (decision === 'allow-session') {
        for (const name of TOOLS_REQUIRING_APPROVAL) {
          this.sessionApprovedTools.add(name);
        }
      }
    }

    // Tool limit check (warn but never block)
    const limitCheck = ctx.scratchpad.canCallTool(toolName, toolQuery);
    if (limitCheck.warning) {
      yield { type: 'tool_limit', tool: toolName, warning: limitCheck.warning, blocked: false };
    }

    yield { type: 'tool_start', tool: toolName, args: toolArgs, toolCallId };

    const toolStartTime = Date.now();

    try {
      const tool = this.toolMap.get(toolName) ?? this.activeToolsProvider?.().find(t => t.name === toolName);
      if (!tool) {
        throw new Error(`Tool '${toolName}' not found`);
      }

      const channel = createProgressChannel();
      const config = {
        metadata: {
          onProgress: channel.emit,
          runContext: ctx,
          ...(this.requestUserInput ? { onUserInput: this.requestUserInput } : {}),
        },
        ...(this.signal ? { signal: this.signal } : {}),
      };

      const toolPromise = withCreditScope(() => tool.invoke(toolArgs, config)).then(
        (raw) => { channel.close(); return raw; },
        (err) => { channel.close(); throw err; },
      );

      for await (const message of channel) {
        yield { type: 'tool_progress', tool: toolName, message, toolCallId } as ToolProgressEvent;
      }

      const { result: rawResult, usage: creditUsage } = await toolPromise;
      const result = typeof rawResult === 'string' ? rawResult : JSON.stringify(rawResult);
      const duration = Date.now() - toolStartTime;

      yield { type: 'tool_end', tool: toolName, args: toolArgs, result, duration, creditUsage, toolCallId };

      ctx.scratchpad.recordToolCall(toolName, toolQuery);
      ctx.scratchpad.addToolResult(toolName, toolArgs, result);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (isBillingError(errorMessage)) {
        markCreditExhausted(ctx, toolName, errorMessage);
      }
      yield { type: 'tool_error', tool: toolName, error: errorMessage, toolCallId };

      ctx.scratchpad.recordToolCall(toolName, toolQuery);
      ctx.scratchpad.addToolResult(toolName, toolArgs, `Error: ${errorMessage}`);
    }
  }

  /** Whether a name matches a runtime-activated (MCP) tool. */
  private isActiveTool(name: string): boolean {
    return this.activeToolsProvider?.().some(t => t.name === name) ?? false;
  }

  /**
   * Skills are exposed through the single `skill` tool. If the model emits a
   * tool call named after a known skill, route it through `skill` instead of
   * failing with "Tool '<skill>' not found".
   */
  private normalizeSkillToolCall(call: ToolCall): ToolCall {
    if (call.name === 'skill' || this.toolMap.has(call.name) || this.isActiveTool(call.name)) {
      return call;
    }

    const skill = discoverSkills().find((candidate) => candidate.name === call.name);
    if (!skill) {
      return call;
    }

    const args = call.args as Record<string, unknown>;
    return {
      ...call,
      name: 'skill',
      args: {
        skill: skill.name,
        ...(Object.keys(args).length > 0 ? { args: JSON.stringify(args) } : {}),
      },
    };
  }

  private extractQueryFromArgs(args: Record<string, unknown>): string | undefined {
    const queryKeys = ['query', 'search', 'question', 'q', 'text', 'input'];
    for (const key of queryKeys) {
      if (typeof args[key] === 'string') {
        return args[key] as string;
      }
    }
    return undefined;
  }

  private requiresApproval(toolName: string): boolean {
    return (TOOLS_REQUIRING_APPROVAL as readonly string[]).includes(toolName);
  }
}
